import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { buildBulkEditTargets, selectedServiceTypeNames } from './BulkEdit/bulkEditTargets';
import { canBulkStatusJobs, canBulkUpdateJobs } from './bulkPermissions';
import { InteractionDialog } from './InteractionDialog';
import { ListActionDialogs } from './ListActionDialogs';
import { EMPTY_FILTERS, filterModelToFilterState } from './serviceListFilters';
import { ServiceListHeader } from './ServiceListHeader';
import { useListJobActions } from './useListJobActions';
import { useRecentJobs } from './useRecentJobs';
import { useReports } from './useReports';
import { useServiceFilterListOptions } from './useServiceFilterListOptions';
import { useServiceListData } from './useServiceListData';
import { ActionPermissions, ListType } from '../../@types';
import { mapServiceFilterOperatorForWire, prepareColumnSelectionForBFF } from '../../api/services';
import { PageContainer } from '../../components/atoms/PageContainer';
import { BulkActionsBar, DEFAULT_ACTION_BUTTONS } from '../../components/molecules/BulkActionsBar';
import { FilterBar, FilterState } from '../../components/molecules/FilterBar';
import { FILTER_FIELD_DEFINITIONS } from '../../components/molecules/FilterBar/AddFilterModal.types';
import { TablePagination, AutoRefresh } from '../../components/molecules/Pagination';
import { PreviewDrawer } from '../../components/molecules/PreviewDrawer';
import { RecentJobsPanel } from '../../components/molecules/RecentJobs';
import { ReportTabs, SaveReportDialog } from '../../components/molecules/ReportTabs';
import { ServiceTable, SortModel } from '../../components/organisms/ServiceTable';
import { ColumnSelection } from '../../components/templates/ColumnSelection';
import { PLATFORM_NAME } from '../../configs/common';
import { SavedReportTypeList } from '../../configs/enums';
import { isUserHasPermission } from '../../configs/permissions';
import { TabHandler } from '../../handlers/TabHandler';
import { useClientVisibleFieldGuard } from '../../hooks/useClientVisibleFieldGuard';
import { useIsClient } from '../../hooks/useIsClient';
import { useListExport } from '../../hooks/useListExport';
import { usePinnedReports } from '../../hooks/usePinnedReports';
import { useReportsModal } from '../../hooks/useReportsModal';
import { useSessionColumnPersist } from '../../hooks/useSessionColumnPersist';
import { useGetUserSystemPermissions } from '../../queries/user-query';
import { clearSessionColumnState } from '../../utils/sessionColumnState';
import { toast } from '../../utils/toast';
import { ReportsModal } from '../Reports/MyReportsList/ReportsModal';

/** Inner component -- remounted via key when the path changes. */
const ServiceListInner: React.FC = () => {
  const theme = useTheme();
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const permissions = getUserPermissionsQuery.data;
  const canCreateService = isUserHasPermission(ActionPermissions.Service_Create, permissions);
  const canBulkUpdate = canBulkUpdateJobs(permissions);
  const canBulkStatus = canBulkStatusJobs(permissions);
  // useIsClient is fail-closed: returns true while the visibility query is
  // still loading or has errored. A brief "client view" flash for a
  // non-client user is preferable to momentarily exposing action surfaces
  // to a real client/customer.
  const isClient = useIsClient();
  const { pinnedCodes, setPinnedCodes, defaultTabIndex, reorderTabs } =
    usePinnedReports('ServiceList');

  const {
    data,
    isFetching,
    page,
    pageSize,
    total,
    fetchDurationMs,
    sortModel,
    columns,
    columnSelectionData,
    filterModel,
    statusOptions,
    statusGroups,
    jobTypeOptions,
    contractOptions,
    contractNameOptions,
    setPage,
    setPageSize,
    setSortModel,
    applyFilters,
    applyReportConfig,
    updateColumnSelection,
    reorderColumns,
    resetColumnsToDefault,
    restoreDefaultColumns,
    setColumnWidth,
    refetch,
    multiSearchQuery,
    openStatusFilterModel,
    openStatusNames,
    openStatusFilterState,
    statusCodeToName,
    statusNameToCodes,
    clientIdToName,
    clientNameToId,
  } = useServiceListData(isClient);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [previewId, setPreviewId] = useState<number | null>(null);

  // Referential stability matters here: this object feeds useReports' apply-
  // report effect dependencies. An inline literal re-triggers that effect on
  // every render once a report tab is open — and because the effect itself
  // causes a re-render (setFilterBarKey), that loops forever, firing an
  // aborted /servicesList request per cycle.
  const filterStateLookups = useMemo(
    () => ({
      statusNameToCodes,
      statusCodeToName,
      clientNameToId,
      clientIdToName,
    }),
    [statusNameToCodes, statusCodeToName, clientNameToId, clientIdToName]
  );

  // Seed FilterBar state from the initial filter model so deep-links like
  // `?structureNodeId=123` (from the structures list "View Jobs" action) are
  // reflected as visible chips rather than silent server-side filters.
  const [filters, setFilters] = useState<FilterState>(() =>
    filterModel.items.length > 0
      ? filterModelToFilterState(filterModel, filterStateLookups)
      : EMPTY_FILTERS
  );
  const [filterBarKey, setFilterBarKey] = useState(0);

  // When the page is hydrated from URL chip-state (back-navigation), the
  // filters useState initializer above may run before the status/client lookup
  // maps have resolved. This effect re-runs once the maps arrive and converts
  // any name-based predicates in the URL to their code/id equivalents so the
  // dropdown checkboxes show the correct ticks.
  const lookupRehydratedRef = useRef(false);
  useEffect(() => {
    if (lookupRehydratedRef.current) return;
    if (filterModel.items.length === 0) return;
    if (Object.keys(statusNameToCodes).length === 0 && Object.keys(clientNameToId).length === 0)
      return;
    lookupRehydratedRef.current = true;
    setFilters(filterModelToFilterState(filterModel, filterStateLookups));
    // Intentionally omit filterModel from deps — we only rehydrate the
    // initial URL-derived state once when lookups first resolve; excluding it
    // prevents user-driven filter changes from being overwritten. The lookup
    // maps are the inputs to `filterStateLookups`, so listing them separately
    // would only add re-runs the ref guard already discards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStateLookups]);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(0);
  const [columnSelectionOpen, setColumnSelectionOpen] = useState(false);

  const {
    savedReports,
    activeReport,
    activeReportName,
    defaultTabLabel,
    saveDialogOpen,
    handleReportChange,
    handleSaveNewReport,
    handleOverwriteReport,
    handleSetDefault,
    setSaveDialogOpen,
  } = useReports({
    filterModel,
    pageSize,
    sortModel,
    columns,
    columnSelectionData,
    applyFilters,
    applyReportConfig,
    resetColumnsToDefault,
    setFilters,
    setFilterBarKey,
    openStatusFilterModel,
    openStatusFilterState,
    filterStateLookups,
    isClient,
  });

  const { recentCodes, isReportsModalOpen, openReportsModal, closeReportsModal } = useReportsModal(
    SavedReportTypeList.ServiceList,
    activeReport
  );

  // Persist the default-view column layout to the browser session — debounced so
  // a resize drag writes once on release, not on every frame. Skipped while a
  // saved report is active — that layout belongs to the report, not the session.
  useSessionColumnPersist('ServiceList', columns, !activeReport);

  // Default tab context-menu actions.

  const handleRevertToDefaultReport = useCallback(
    () => handleReportChange(null),
    [handleReportChange]
  );
  const handleRevertToSystemDefaults = useCallback(() => {
    const current = savedReports.find((r) => r.isDefault);
    if (current) handleSetDefault({ ...current, isDefault: false });
    clearSessionColumnState('ServiceList');
    // Apply the page's system-default filter (open statuses), matching
    // useReports.handleReportChange(null). resetColumnsToDefault runs last so it
    // keeps the widths.
    if (openStatusFilterModel.items.length > 0) {
      applyReportConfig(openStatusFilterModel);
      setFilters(openStatusFilterState);
    } else {
      setFilters(EMPTY_FILTERS);
      applyFilters(EMPTY_FILTERS);
    }
    resetColumnsToDefault();
  }, [
    savedReports,
    handleSetDefault,
    applyReportConfig,
    applyFilters,
    openStatusFilterModel,
    openStatusFilterState,
    setFilters,
    resetColumnsToDefault,
  ]);

  const { recentJobs, recentJobsAnchor, recentFilter, setRecentJobsAnchor, setRecentFilter } =
    useRecentJobs(canCreateService);

  const jobActions = useListJobActions(refetch);

  TabHandler(`Jobs | ${PLATFORM_NAME}`);

  useEffect(() => {
    if (autoRefreshInterval <= 0) return;
    const timer = setInterval(() => refetch(), autoRefreshInterval * 1000);
    return () => clearInterval(timer);
  }, [autoRefreshInterval, refetch]);

  const { exportAnchor, setExportAnchor, handleExport } = useListExport({
    fileName: ListType.ServiceList,
    columns,
    columnSelectionData,
    // Mirror the list query's wire transform: api-services keys field
    // projection by section title, but the UI labels the "Service" group
    // "Job". Without the Job→Service remap the BFF drops every gated
    // column in that section (reportedFault, reportedFaultDescription,
    // contractName, …) and they export blank. See getAllServices.
    transformColumnSelection: prepareColumnSelectionForBFF,
    filterModel,
    // Apply the same numeric-column operator fix the list query uses, so
    // an exported list with a "Job ID equals N" filter matches what's on
    // screen instead of erroring on the BFF (see mapServiceFilterOperatorForWire).
    mapFilterItems: (items) =>
      items.map((item) => ({
        ...item,
        operator: mapServiceFilterOperatorForWire(item.field, item.operator) ?? item.operator,
      })),
    rowCount: total,
    pageSize,
    sorting: [{ field: sortModel.field, sort: sortModel.sort }],
    multiSearchQuery,
  });

  const handleSearch = useCallback(
    (newFilters: FilterState) => {
      // Always merge in the current lookup maps so buildFilterModel can convert
      // status codes and client ids back to the names the BFF expects, regardless
      // of what the FilterBar's local state happens to carry.
      const enriched: FilterState = {
        ...newFilters,
        statusCodeToName,
        contractIdToName: clientIdToName,
      };
      setFilters(enriched);
      applyFilters(enriched);
    },
    [applyFilters, statusCodeToName, clientIdToName]
  );

  // Lock client/customer users into filtering and sorting by the columns
  // their curated view actually exposes. Non-clients get pass-through (no
  // restriction, original sort handler). The visible-fields list is derived
  // from the live `columns` so saved-report column toggles flow through to
  // the filter catalog and sort guard automatically.
  const visibleFields = useMemo(
    () => columns.filter((c) => c.visible).map((c) => c.key),
    [columns]
  );
  const { restrictedFieldDefinitions, guardSortChange } = useClientVisibleFieldGuard(
    FILTER_FIELD_DEFINITIONS,
    visibleFields
  );

  const handleSortChange = useMemo(
    () =>
      guardSortChange<SortModel>((model) => {
        setSortModel({ field: model.field, sort: model.direction });
      }),
    [guardSortChange, setSortModel]
  );

  const handleBulkAction = useCallback(
    (type: string) => {
      if (type === 'open_tabs') {
        const MAX_TABS = 5;
        const idsToOpen = selectedIds.slice(0, MAX_TABS);
        for (const id of idsToOpen) {
          window.open(`/services/${id}`, `service_${id}`, 'noopener');
        }
        if (selectedIds.length > MAX_TABS) {
          toast.info(
            `Opened ${MAX_TABS} of ${selectedIds.length} jobs. Browsers block opening too many tabs at once.`
          );
        }
        return;
      }
      jobActions.openBulkAction(type, selectedIds);
    },
    [selectedIds, jobActions]
  );

  const handleRowAction = useCallback(
    (id: number, action: string) => {
      if (action === 'preview') {
        setPreviewId(id);
        return;
      }
      jobActions.openRowAction(id, action);
    },
    [jobActions]
  );

  const hasSearchPermission = isUserHasPermission(ActionPermissions.Service_Search, permissions);
  const canViewStructure = isUserHasPermission(
    ActionPermissions.Structure_Edit_View_Structure_Location,
    permissions
  );
  const filterListOptions = useServiceFilterListOptions(hasSearchPermission);
  const filterFieldConfig = useMemo(
    () => ({
      listOptions: filterListOptions,
      // For client/customer users, restrict the Add Filter modal's field
      // catalog to the columns the curated report exposes. Non-clients see
      // the full catalog (restrictedFieldDefinitions is undefined).
      ...(restrictedFieldDefinitions ? { fieldDefinitions: restrictedFieldDefinitions } : {}),
    }),
    [filterListOptions, restrictedFieldDefinitions]
  );
  const tableData = useMemo(() => data ?? [], [data]);

  // Offering Bulk Update to someone who can't use it just opens a dialog whose
  // Apply is disabled — hide the button instead.
  const bulkBarActions = useMemo(
    () =>
      DEFAULT_ACTION_BUTTONS.filter((action) => {
        if (action.key === 'bulk_update') return canBulkUpdate;
        if (action.key === 'update_status') return canBulkStatus;
        return true;
      }),
    [canBulkUpdate, canBulkStatus]
  );

  // Derived from the ids the dialog was opened with, not the live selection:
  // the header entry point deliberately opens with none (file mode), and a
  // selection that changes while the dialog is open must not move its targets.
  const bulkEditTargets = useMemo(
    () => buildBulkEditTargets(jobActions.targetIds, tableData, openStatusNames),
    [jobActions.targetIds, tableData, openStatusNames]
  );

  // A mixed selection narrows the status options via the intersection, which
  // is otherwise invisible to the user.
  const selectedServiceTypes = useMemo(
    () => selectedServiceTypeNames(jobActions.targetIds, tableData),
    [jobActions.targetIds, tableData]
  );

  const tableSortModel = useMemo<SortModel>(
    () => ({
      field: sortModel.field,
      direction: (sortModel.sort as 'asc' | 'desc') ?? 'desc',
    }),
    [sortModel.field, sortModel.sort]
  );

  return (
    <>
      <ServiceListHeader
        total={total}
        permissions={permissions}
        exportAnchor={exportAnchor}
        onExportClick={(e) => setExportAnchor(e.currentTarget)}
        onExportClose={() => setExportAnchor(null)}
        onExportCsv={() => handleExport('csv')}
        onExportExcel={() => handleExport('xlsx')}
        onRecentJobsClick={(e) => setRecentJobsAnchor(e.currentTarget)}
        onColumnsClick={isClient ? undefined : () => setColumnSelectionOpen(true)}
        onReportsClick={openReportsModal}
        onBulkUpdateClick={
          // No selection: the dialog opens on the file tab, where the uploaded
          // spreadsheet names the jobs.
          isClient ? undefined : () => jobActions.openBulkAction('bulk_update', [])
        }
      />
      <PageContainer>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <ReportTabs
            activeReport={activeReport}
            reports={savedReports}
            onReportChange={handleReportChange}
            recentCodes={recentCodes}
            onSave={isClient ? undefined : () => setSaveDialogOpen(true)}
            defaultTabLabel={defaultTabLabel}
            onSetDefault={isClient ? undefined : handleSetDefault}
            pinnedCodes={pinnedCodes}
            onPinnedCodesChange={setPinnedCodes}
            defaultTabIndex={defaultTabIndex}
            onReorderTabs={reorderTabs}
            onRevertToSystemDefaults={isClient ? undefined : handleRevertToSystemDefaults}
            onRevertToDefaultReport={isClient ? undefined : handleRevertToDefaultReport}
          />
          {!isClient && (
            <SaveReportDialog
              open={saveDialogOpen}
              onClose={() => setSaveDialogOpen(false)}
              onSaveNew={handleSaveNewReport}
              onOverwrite={handleOverwriteReport}
              activeReportName={activeReportName}
            />
          )}

          {hasSearchPermission ? (
            <>
              <FilterBar
                key={filterBarKey}
                onSearch={handleSearch}
                statuses={statusOptions}
                statusGroups={statusGroups}
                jobTypes={jobTypeOptions}
                contracts={contractOptions}
                contractNames={contractNameOptions}
                labels={{
                  contractsDropdown: 'Client',
                  hideContractsDropdown: isClient,
                  // Unlike Client, Contract stays visible for client/customer
                  // users — they see a single client but often several
                  // contracts, so the filter is still useful to them.
                  contractNamesDropdown: 'Contract',
                }}
                initialFilters={filters}
                fieldConfig={filterFieldConfig}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: '20px',
                  py: '3px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    fontSize: '12px',
                    color: 'text.secondary',
                  }}
                >
                  {total > 0 && (
                    <span>
                      Showing {page * pageSize + 1}&ndash;{Math.min((page + 1) * pageSize, total)}{' '}
                      of {total.toLocaleString()}
                    </span>
                  )}
                  {fetchDurationMs !== null && (
                    <span>Took {(fetchDurationMs / 1000).toFixed(2)}s</span>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '12px',
                    color: 'text.secondary',
                  }}
                >
                  <label htmlFor="rows-per-page-select">Rows:</label>
                  <Select
                    id="rows-per-page-select"
                    size="small"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(0);
                    }}
                    aria-label="Rows per page"
                    sx={{
                      fontSize: '12px',
                      height: 26,
                      '& .MuiSelect-select': { py: 0.25, px: 1 },
                    }}
                  >
                    {[25, 50, 100].map((size) => (
                      <MenuItem key={size} value={size} sx={{ fontSize: '12px' }}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
              {/* One selected job is a legitimate bulk edit — the old ">= 2"
                  floor made single-job edits unreachable from the list.
                  Still hidden while a bulk dialog is open: the bar now sits
                  below the modal layer so it no longer covers one, but its
                  actions are meaningless mid-dialog. The column picker is
                  deliberately not in this gate — it edits the view, not the
                  selection, so the bar stays put (dimmed) behind it. */}
              {!isClient && selectedIds.length >= 1 && jobActions.activeDialog === null && (
                <BulkActionsBar
                  count={selectedIds.length}
                  onAction={handleBulkAction}
                  onClear={() => setSelectedIds([])}
                  actions={bulkBarActions}
                />
              )}
              <ServiceTable
                data={tableData}
                columns={columns}
                sortModel={tableSortModel}
                onSortChange={handleSortChange}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                onPreview={(id) => setPreviewId(id)}
                onRowAction={handleRowAction}
                onColumnReorder={reorderColumns}
                onColumnResize={setColumnWidth}
                loading={isFetching}
                canViewStructure={canViewStructure}
                isClientCustomer={isClient}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: '20px',
                  pb: 2,
                }}
              >
                <Box sx={{ width: 160 }} />
                <TablePagination
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
                <AutoRefresh interval={autoRefreshInterval} onChange={setAutoRefreshInterval} />
              </Box>
            </>
          ) : (
            <Typography sx={{ mt: 4, px: '20px', color: theme.palette.text.secondary }}>
              You do not have permission to view services.
            </Typography>
          )}

          <ListActionDialogs
            activeDialog={jobActions.activeDialog}
            targetIds={jobActions.targetIds}
            bulkEditTargets={bulkEditTargets}
            onRefresh={refetch}
            onClose={jobActions.closeDialog}
            statusOptions={jobActions.statusOptions}
            statusLoading={jobActions.statusLoading}
            onSubmitStatus={jobActions.submitStatus}
            onSubmitAllocation={jobActions.submitAllocation}
            onSubmitSchedule={jobActions.submitSchedule}
            onSubmitNote={jobActions.submitNote}
            submitting={jobActions.submitting}
            lastError={jobActions.lastError}
            isBulkRun={jobActions.isBulkRun}
            serviceTypeNames={selectedServiceTypes}
            progress={jobActions.progress}
            failures={jobActions.failures}
          />
          <InteractionDialog
            interactions={jobActions.pendingInteractions}
            permissions={permissions}
            onComplete={jobActions.clearPendingInteractions}
            onCancel={jobActions.cancelPendingInteractions}
          />
          <PreviewDrawer
            serviceId={previewId}
            open={previewId !== null}
            onClose={() => setPreviewId(null)}
            onAction={(type) => {
              if (previewId === null) return;
              const map: Record<string, string> = {
                addNote: 'add_note',
                assign: 'assign_tech',
                schedule: 'schedule',
                status: 'update_status',
              };
              jobActions.openRowAction(previewId, map[type] ?? type);
            }}
          />
          <RecentJobsPanel
            anchorEl={recentJobsAnchor}
            open={Boolean(recentJobsAnchor)}
            onClose={() => setRecentJobsAnchor(null)}
            jobs={recentJobs}
            filter={recentFilter}
            onFilterChange={setRecentFilter}
            canViewCreated={canCreateService}
          />
          {!isClient && (
            <ColumnSelection
              open={columnSelectionOpen}
              title="Select Columns"
              value={columnSelectionData}
              onChange={updateColumnSelection}
              onClose={() => setColumnSelectionOpen(false)}
              onReset={restoreDefaultColumns}
            />
          )}
          <ReportsModal
            open={isReportsModalOpen}
            onClose={closeReportsModal}
            pageName={SavedReportTypeList.ServiceList}
            onSelectReport={handleReportChange}
          />
        </Box>
      </PageContainer>
    </>
  );
};

/** Outer wrapper that remounts the inner component when the route path changes. */
export const ServiceList: React.FC = () => {
  const location = useLocation();
  return <ServiceListInner key={location.pathname} />;
};
